import { isEmpty } from 'lodash';

import { VALUE_PREFIX, ShapeConstants, GradientType } from '../constant';
import { alphaHexToRgba, removePrefix } from '../util';

import type { Fill, PureFill, GradientFill, ImageFill, GradientStop } from '../types/shape';
import type { Background } from '../types/css';
import { PENPOT_URL } from '../../../config/urlConfig';

/**
 * 转换fills为background.
 *
 * @remarks
 * background是一个属性家族，含有一系列相关属性，目前只转换background-color和background-image，后续若有需要可以扩展补充其他属性.
 *
 * @param fills - fills属性对象数组.
 * @returns background属性对象.
 */
export function transFillsToBackground(fills: Fill[]): Background {
  const hasFills = !isEmpty(fills);

  if (!hasFills) {
    return null;
  }

  // 存储纯色单值.
  let backgroundColor: string = '';

  // 存储渐变和图片多值.
  const backgroundImageValues: string[] = [];

  // 遍历fills，根据类型分别处理.
  for (let fill of fills) {
    const isPureFill = !!fill[ShapeConstants.fillColor];
    const isGradientFill = !!fill[ShapeConstants.fillColorGradient];
    const isImageFill = !!fill[ShapeConstants.fillImage];

    if (isPureFill) {
      if (backgroundColor) {
        continue; // 若纯色背景已添加过，则其他均忽略.
      }
      backgroundColor = transPureFillToBackground(fill as PureFill); // 纯色处理逻辑.
    } else if (isGradientFill) {
      backgroundImageValues.push(transGradientFillToBackground(fill as GradientFill)); // 渐变处理逻辑.
    } else if (isImageFill) {
      backgroundImageValues.push(transImageFillToBackground(fill as ImageFill)); // 图片处理逻辑.
    }
  }

  // 初始化结果集.
  const background: Background = {};

  // 非空判断，直接赋值background-color.
  if (backgroundColor) {
    background.backgroundColor = backgroundColor;
  }

  // 非空判断，逗号拼接background-image.
  if (backgroundImageValues.length) {
    background.backgroundImage = backgroundImageValues.join(', ');
    background.backgroundRepeat = 'no-repeat';
    background.backgroundPosition = 'center';
    background.backgroundPositionX = 'center';
    background.backgroundPositionY = 'center';
    background.backgroundSize = 'cover';
  }

  return background;
}

/**
 * 转换纯色对象fill为rgba函数字符串.
 *
 * @param fill - 纯色对象.
 * @returns rgba函数字符串.
 */
function transPureFillToBackground(fill: PureFill): string {
  return alphaHexToRgba(fill);
}

/**
 * 转换渐变对象fill为相应CSS函数字符串.
 *
 * @param fill - 渐变对象.
 * @returns CSS函数字符串.
 */
function transGradientFillToBackground(fill: GradientFill): string {
  const gradientType =_get(fill, [ShapeConstants.fillColorGradient, ShapeConstants.type]);

  if (gradientType === GradientType.Linear) {
    return transLinearGradientFillToBackground(fill as GradientFill); // 线性渐变处理逻辑.
  } else if (gradientType === GradientType.Radial) {
    return transRadialGradientFillToBackground(fill as GradientFill); // 径向渐变处理逻辑.
  } else {
    return null;
  }
}

/**
 * 转换线性渐变对象fill为linear-gradient函数字符串.
 *
 * 1.根据渐变轴两个端点的坐标计算出渐变轴的倾斜角度degrees;
 * 2.遍历色标数组stops转换色标;
 * 3.最后拼接为完整的linear-gradient函数字符串;
 *
 * @param fill - 线性渐变对象.
 * @returns linear-gradient函数字符串.
 */
function transLinearGradientFillToBackground(fill: GradientFill): string {
  const x1 =_get(fill, [ShapeConstants.fillColorGradient, ShapeConstants.startX]);
  const x2 =_get(fill, [ShapeConstants.fillColorGradient, ShapeConstants.endX]);
  const y1 =_get(fill, [ShapeConstants.fillColorGradient, ShapeConstants.startY]);
  const y2 =_get(fill, [ShapeConstants.fillColorGradient, ShapeConstants.endY]);

  // 先计算弧度.
  const radians = Math.atan2(x1 - x2, y1 - y2);

  // 再根据弧度计算出角度，角度范围: [0, 360).
  const degrees = (radians * (180 / Math.PI) * -1);

  const stops: GradientStop[] =_get(fill, [ShapeConstants.fillColorGradient, ShapeConstants.stops], []);
  // 遍历并转换色标，并过滤掉空值.
  const stopValues: string[] = stops.map((stop: GradientStop): string => {
    try {
      const color = alphaHexToRgba({
        [ShapeConstants.fillColor]: stop[ShapeConstants.color],
        [ShapeConstants.fillOpacity]: stop[ShapeConstants.opacity],
      } as PureFill);

      const lengthPercentage = `${stop[ShapeConstants.offset] * 100}%`;

      return `${color} ${lengthPercentage}`;
    } catch (e) {
      return null;
    }
  }).filter(stopValue => !!stopValue);

  const hasValue = stopValues.length > 0;

  if (hasValue) {
    return `linear-gradient(${degrees}deg, ${stopValues.join(', ')})`;
  } else {
    return null;
  }
}

/**
 * 转换径向渐变对象fill为radial-gradient函数字符串.
 *
 * @param fill - 线性渐变对象.
 * @returns radial-gradient函数字符串.
 */
function transRadialGradientFillToBackground(fill: GradientFill): string {
  const stops: GradientStop[] =_get(fill, [ShapeConstants.fillColorGradient, ShapeConstants.stops], []);
  // 遍历并转换色标，并过滤掉空值.
  const stopValues: string[] = stops.map((stop: GradientStop): string => {
    try {
      const color = alphaHexToRgba({
        [ShapeConstants.fillColor]: stop[ShapeConstants.color],
        [ShapeConstants.fillOpacity]: stop[ShapeConstants.opacity],
      } as PureFill);

      const lengthPercentage = `${stop[ShapeConstants.offset] * 100}%`;

      return `${color} ${lengthPercentage}`;
    } catch (e) {
      return null;
    }
  }).filter(stopValue => !!stopValue);

  const hasValue = stopValues.length > 0;

  if (hasValue) {
    return `radial-gradient(${stopValues.join(', ')})`;
  } else {
    return null;
  }
}

/**
 * 转换图片对象fill为url函数字符串.
 *
 * 1.根据UI设计器url源地址和图片资源id拼接成完整的资源获取地址;
 * 2.若透明度小于1，前置一个白色蒙层模拟;
 * 3.最后拼接为完整的函数字符串;
 *
 * @param fill - 图片对象.
 * @returns url函数字符串.
 */
function transImageFillToBackground(fill: ImageFill): string {
  const fillImageId: string =_get(fill, [ShapeConstants.fillImage, ShapeConstants.id]);
  const fillOpacity: number =_get(fill, [ShapeConstants.fillOpacity]);

  // UI设计器url源地址.
  const penpotURLOrigin = PENPOT_URL;

  // 图片资源id.
  const mediaId: string = removePrefix(fillImageId, VALUE_PREFIX);

  // 透明度小于1则需要前置蒙层.
  const needMask: boolean = fillOpacity < 1;

  if (needMask) {
    return `linear-gradient(to bottom, rgba(255, 255, 255, ${fillOpacity}) 0%,rgba(255, 255, 255, ${fillOpacity}) 0%), url("${penpotURLOrigin}/assets/by-file-media-id/${mediaId}")`;
  } else {
    return `url("${penpotURLOrigin}/assets/by-file-media-id/${mediaId}")`;
  }
}
